import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { isAuthenticated } from '@/api/client';
import Sidebar from '@/components/layout/Sidebar';
import AppErrorBoundary from '@/components/errors/AppErrorBoundary';

// Eager: small pages
import Login from '@/pages/Login';
import NotFound from '@/pages/errors/NotFound';
import Forbidden from '@/pages/errors/Forbidden';
import ServerError from '@/pages/errors/ServerError';
import Unauthorized from '@/pages/errors/Unauthorized';
// Lazy: heavy pages (code-split)
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Workflows = lazy(() => import('@/pages/Workflows'));
const WorkflowDetail = lazy(() => import('@/pages/WorkflowDetail'));
const WorkflowEditor = lazy(() => import('@/pages/WorkflowEditor'));
const DLQ = lazy(() => import('@/pages/DLQ'));
const Metrics = lazy(() => import('@/pages/Metrics'));
const Schedules = lazy(() => import('@/pages/Schedules'));
const AgentConfig = lazy(() => import('@/pages/AgentConfig'));
const MemoryItems = lazy(() => import('@/pages/MemoryItems'));
const Services = lazy(() => import('@/pages/Services'));
const CodeGraph = lazy(() => import('@/pages/CodeGraph'));
const MemoryAgents = lazy(() => import('@/pages/MemoryAgents'));
const Benchmarks = lazy(() => import('@/pages/Benchmarks'));
const Architecture = lazy(() => import('@/pages/Architecture'));
const Repository = lazy(() => import('@/pages/Repository'));
const Settings = lazy(() => import('@/pages/Settings'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5_000,
      retry: 1,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function Loading() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100%', minHeight: 300, color: 'var(--text-2)', fontSize: 14,
    }}>
      Loading...
    </div>
  );
}

function AppShell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar />
      <main style={{
        marginLeft: 'var(--sidebar-width)',
        flex: 1,
        minHeight: '100vh',
        background: 'var(--bg-0)',
      }}>
        <AppErrorBoundary>
          <Suspense fallback={<Loading />}>
            {children}
          </Suspense>
        </AppErrorBoundary>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/401" element={<Unauthorized />} />
          <Route path="/403" element={<Forbidden />} />
          <Route path="/500" element={<ServerError />} />

          <Route path="/*" element={
            <ProtectedRoute>
              <AppShell>
                <Routes>
                  <Route path="/" element={<Dashboard />} />

                  {/* Orchestration */}
                  <Route path="/workflows" element={<Workflows />} />
                  <Route path="/workflows/:id" element={<WorkflowDetail />} />
                  <Route path="/editor" element={<WorkflowEditor />} />
                  <Route path="/dlq" element={<DLQ />} />
                  <Route path="/agent-config" element={<AgentConfig />} />
                  <Route path="/schedules" element={<Schedules />} />
                  <Route path="/metrics" element={<Metrics />} />

                  {/* Memory System */}
                  <Route path="/memory" element={<MemoryItems />} />
                  <Route path="/code-graph" element={<CodeGraph />} />
                  <Route path="/memory-agents" element={<MemoryAgents />} />
                  <Route path="/benchmarks" element={<Benchmarks />} />
                  <Route path="/services" element={<Services />} />

                  {/* System */}
                  <Route path="/architecture" element={<Architecture />} />
                  <Route path="/repository" element={<Repository />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </AppShell>
            </ProtectedRoute>
          } />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
